// src/components/chat/Sidebar.jsx
//
// Fixed 72px LEFT icon sidebar (full height) — replaces the old TopToolbar
// as the messenger shell chrome. Replicates design_specs/dashboard_v7.html
// exactly: rgba(5,5,5,.95) shell, 1px rgba(0,255,153,.30) right border,
// 48px tenant logo, 40px nav icons with neon active-glow + slide-in pill
// tooltips, and a bottom utility cluster (role views · notifications ·
// settings · logout).
//
// On <=720px the SAME element flips to a horizontal bottom bar (see the
// injected @media block) — this supersedes the old MobileBottomNav in the
// layout. Tenant logo, dividers and spacers hide on mobile per the spec.
//
// The former left FAB stack (Team/Admin/Exec) + back-to-chat FAB now live
// in this sidebar; the global KIRA float (bottom-right, via KiraGate) is
// untouched.

import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,   // Dashboard  — 4 rounded squares
  MessageCircle,     // Chat       — rounded speech bubble
  Clock,             // Attendance — clock
  Calendar,          // Leave      — calendar
  CreditCard,        // Payroll    — card
  TrendingUp,        // Performance— line-up + arrow
  Ticket,            // Tickets    — ticket
  Folder,            // Projects   — folder
  GraduationCap,     // Learning   — mortar board
  Gavel,             // Cases      — ER case management (gated; see below)
  Rocket,            // Onboarding — kept from the current toolbar
  Users,             // Team view  — two people
  ShieldCheck,       // Admin view — shield + check
  Briefcase,         // Exec view  — briefcase
  Bell,              // Notifications
  Settings,          // Settings   — gear
  LogOut,            // Logout     — door + arrow
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import useOrgModules from "../../hooks/useOrgModules";
import NotificationDropdown from "../notifications/NotificationDropdown";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const ICON = 19;         // spec: .nv svg { width/height 19 }
const STROKE = 1.8;      // spec: stroke-width 1.8

// ── nav modules (spec order). `staff`/`team`/`admin` mirror the FabStack
// route maps so the active-glow follows you into a team/admin view and the
// role-view icons can navigate to the current page's team/admin variant.
const NAV = [
  { key: "dashboard",   label: "Dashboard",   Icon: LayoutDashboard, module: null,          staff: "/messenger/dashboard",   team: "/manager-dashboard",       admin: "/admin/dashboard",   extra: ["/executive/dashboard"] },
  { key: "chat",        label: "Chat",        Icon: MessageCircle,   module: null,          staff: "/chat" },
  { key: "attendance",  label: "Attendance",  Icon: Clock,           module: "attendance",  staff: "/messenger/attendance",  team: "/manager/team-attendance", admin: "/admin/attendance" },
  { key: "leave",       label: "Leave",       Icon: Calendar,        module: "leave",       staff: "/messenger/leave",       team: "/manager/team-leave",      admin: "/admin/leave" },
  { key: "payroll",     label: "Payroll",     Icon: CreditCard,      module: "payroll",     staff: "/messenger/payroll",     team: "/manager/team-payroll",    admin: "/admin/payroll" },
  // adminHome: an admin clicking this icon lands on the ADMIN variant —
  // that module's configuration (Performance > Profiles / Cycles / Goals)
  // lives there and was previously only reachable via the contextual
  // shield, i.e. effectively undiscoverable. Clicking again while already
  // on the admin page returns them to their own staff view, so personal
  // access is preserved. Opt-in per entry: no other module is affected.
  { key: "performance", label: "Performance", Icon: TrendingUp,      module: "performance", staff: "/messenger/performance", team: "/manager/team-performance",admin: "/admin/performance", adminHome: true },
  { key: "tickets",     label: "Tickets",     Icon: Ticket,          module: null,          staff: "/messenger/tickets",     team: "/manager/team-tickets",    admin: "/admin/tickets" },
  { key: "projects",    label: "Projects",    Icon: Folder,          module: "projects",    staff: "/messenger/projects",    team: "/manager/team-projects",   admin: "/admin/projects" },
  { key: "learning",    label: "Learning",    Icon: GraduationCap,   module: "learning",    staff: "/messenger/learning",    team: "/messenger/learning/team", admin: "/admin/learning" },
  // Onboarding — preserved from the current toolbar; only for new starters.
  { key: "onboarding",  label: "Onboarding",  Icon: Rocket,          module: null,          staff: "/messenger/onboarding",  newStarterOnly: true },
];

// ── Cases: the ER door. DELIBERATELY NOT IN THE NAV ARRAY ABOVE.
//
// ============================================================
// READ THIS BEFORE MOVING IT INTO NAV.
// ============================================================
//
// WHO MUST NOT SEE THIS ENTRY. An employee who is the SUBJECT of a
// grievance is a party to an ER case. So is a witness, a reporter and a
// companion. None of them may see a Cases icon in their own sidebar
// BECAUSE OF THAT INVOLVEMENT: an icon appearing in somebody's
// navigation the week a grievance is raised against them is a
// disclosure the employer did not choose to make, cannot take back, and
// made to whoever was standing behind them.
//
// THE BACKEND DECIDES, AND IT ANSWERS TRUE FOR TWO KINDS OF PERSON —
// see er_cases/door_routes.py's nav-entry docstring, which is the
// authority on this and not the paragraph below:
//
//   SOMEBODY HANDLING A CASE — investigator, decision maker, appeal
//   manager, case owner or reviewer, on at least one unclosed case.
//
//   SOMEBODY WHO MAY OPEN ONE — a manager, admin or executive, by
//   er_cases/create.py's CREATE_ROLES. Added by task 17, because until
//   then a manager who could open a case had no route into the module
//   until somebody else put them on one.
//
// THE SECOND BRANCH DOES NOT WEAKEN THE FIRST RULE, and the row that
// looks like it does is a MANAGER who is the subject of a grievance.
// They see the icon — but because they are a manager, on the same terms
// as every other manager in the organisation, whether or not any case
// exists. A signal that every manager has is not a signal. The icon it
// replaces would have appeared the week the grievance was raised and
// vanished when the case closed, and THAT is the disclosure.
//
// A STAFF subject, reporter, witness and companion still get nothing,
// which is the rule this block exists to protect. Never make this
// entry depend on mere INVOLVEMENT in a case. A superadmin gets nothing
// either: they can reach no ER case in any organisation.
//
// WHY IT IS NOT AN ENTRY IN `NAV` WITH A FILTER.
//
// The audit found that the NAV array has no role field and that
// `visibleNav`'s filter is the only thing standing between an entry and
// every user. Two shapes were available:
//
//   (a) a `requiresHandlingRole` field on a NAV entry, plus a clause in
//       the visibleNav filter;
//   (b) a component that renders nothing unless entitled, with no array
//       entry and no filter clause at all.
//
// (a) puts the entry and its gate in two places. A future edit that
// rewrites or reorders that filter — and it has been rewritten before —
// drops the clause and the entry appears for EVERYONE, silently, with
// no error and nothing failing. The blast radius of that mistake is a
// grievance subject seeing a Cases icon.
//
// (b) has no filter to drop. The gate is the component's own first
// line, so the entry cannot render without it, and the worst a careless
// edit can do is make the icon disappear — which is visible, safe, and
// gets reported by the person who needs it.
//
// So: (b). One component, one gate, colocated, used by both shells.

export function useShowCasesEntry() {
  // null = not yet known. Kept distinct from false so the icon does not
  // flash in and out on every mount.
  const [show, setShow] = useState(null);
  const { modules } = useOrgModules();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setShow(false); return; }
    let cancelled = false;
    // GET /api/cases/auth/nav-entry -> { show: boolean }
    //
    // It returns ONE BOOLEAN: no count, no reference, no role, and no
    // field saying which of the two branches answered. A staff
    // subject's `false` is therefore indistinguishable from that of a
    // colleague with no involvement at all, and a manager's `true` says
    // only "you are a manager or you are handling something" — never
    // which. It writes no audit row; see its docstring.
    fetch(`${API_BASE}/api/cases/auth/nav-entry`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setShow(d?.show === true); })
      // Any failure means no entry. There is no fail-open path here and
      // there must not be: an error must never be the reason an icon
      // appears.
      .catch(() => { if (!cancelled) setShow(false); });
    return () => { cancelled = true; };
  }, []);

  // TWO CONDITIONS, AND THE MODULE ONE IS READ FROM `modules` RATHER
  // THAN THROUGH isEnabled().
  //
  // useOrgModules' isEnabled() is FAIL-OPEN by design — hooks/
  // useOrgModules.js: `if (modules[key] === undefined) return true` — so
  // that the UI does not blank out while loading. That default is wrong
  // for this one entry, and modules/routes.py already says so in its
  // DEFAULT_OFF_MODULES comment.
  //
  // In practice /api/modules/my returns EVERY key in ALL_MODULES with an
  // explicit is_enabled (er_cases resolving to false for any org that
  // has not bought it), so the fail-open branch is not reached for this
  // key today. Reading `modules` directly means it stays correct even if
  // that ever changes: an explicit false hides the entry, and anything
  // else falls through to the backend boolean, which is the real gate.
  const moduleNotDisabled = modules?.er_cases !== false;
  return show === true && moduleNotDisabled;
}

/** The Cases icon. Renders NOTHING unless the backend says this person
 *  works on cases — handling one, or entitled to open one — see the
 *  block above for both branches and for why the gate is here rather
 *  than in a filter.
 *
 *  NAVIGATES IN-APP (task 11B). It used to window.open a separate
 *  application on its own origin, where the user signed in a second time
 *  at a door of its own; the paragraphs that described that handoff, the
 *  absent handoff token and the rel="noopener noreferrer" reasoning have
 *  been deleted rather than reworded, because they described machinery
 *  that no longer exists — Kwery_cases_frontend and the cases door are
 *  both gone, and a comment explaining a cross-origin hazard on a route
 *  that is now a client-side navigation is worse than no comment.
 *
 *  What replaced the second door is the step-up gate on /cases: the
 *  ordinary Kwery session, plus a six-digit code confirming the account
 *  holder is present. So the protection is unchanged in substance — a
 *  stolen session still does not open a disciplinary case — while the
 *  user keeps one login, one tab and one session.
 *
 *  IT STILL SUPPLIES ITS OWN onClick, and that is deliberate rather
 *  than incidental. TopToolbar.jsx renders this component in two places
 *  and passes only `render`; changing the contract to require a
 *  navigate prop would have left both of those calling an undefined
 *  function, in the one component whose failure mode is a Cases icon
 *  behaving oddly for somebody handling a disciplinary case. useNavigate
 *  works here because both shells render inside the router. */
export function CasesNavEntry({ render }) {
  const navigate = useNavigate();
  const show = useShowCasesEntry();
  if (!show) return null;
  return render({
    label: "Cases",
    Icon: Gavel,
    onClick: () => navigate("/cases"),
  });
}

function readUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

// pathname (lowercased, trailing slash trimmed) matches a route prefix.
function matches(norm, route) {
  if (!route) return false;
  return norm === route || norm.startsWith(route + "/");
}
function navActive(item, norm) {
  return [item.staff, item.team, item.admin, ...(item.extra || [])]
    .some((r) => matches(norm, r));
}

function initialsFrom(name) {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

// ── org branding: GET /api/org/branding once, cached module-wide so a
// remount doesn't refetch. Returns { name, logo_url } | null (loading/none).
let _brandingCache;
function useOrgBranding() {
  const [branding, setBranding] = useState(_brandingCache || null);
  useEffect(() => {
    if (_brandingCache) return;
    const tok = localStorage.getItem("token");
    if (!tok) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/org/branding`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        _brandingCache = d;
        setBranding(d);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return branding;
}

// ── new-starter check (mirrors the current toolbar's useHasActiveOnboarding)
function useHasActiveOnboarding() {
  const [hasOb, setHasOb] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tok = localStorage.getItem("token");
    if (!tok) { setHasOb(false); return; }
    fetch(`${API_BASE}/api/lifecycle/onboarding/my-tasks`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (!d || !d.onboarding) { setHasOb(false); return; }
        const start = d.onboarding.start_date ? new Date(d.onboarding.start_date) : null;
        if (start) {
          const ageDays = Math.floor((Date.now() - start.getTime()) / 86400000);
          setHasOb(ageDays >= 0 && ageDays < 90);
        } else {
          setHasOb(true);
        }
      })
      .catch(() => !cancelled && setHasOb(false));
    return () => { cancelled = true; };
  }, []);
  return hasOb;
}

// ── mandatory-learning overdue count (mirrors current toolbar). Drives the
// green dot on the Learning icon; the count is surfaced in its tooltip.
function useLearningOverdueCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    let cancelled = false;
    const fetchOnce = () => {
      fetch(`${API_BASE}/api/learning/my-courses`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : []))
        .then((rows) => {
          if (cancelled || !Array.isArray(rows)) return;
          const today = new Date().toISOString().slice(0, 10);
          setCount(rows.filter(
            (e) => e.is_mandatory && e.status !== "completed" && e.due_date && String(e.due_date) < today
          ).length);
        })
        .catch(() => {});
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return count;
}

// ── one nav/util icon: span + CSS pseudo-element tooltip (data-label) + dot.
function IconBtn({ label, active, logout, showDot, onClick, children }) {
  return (
    <span
      className={`kwsb-nv${active ? " is-active" : ""}${logout ? " is-logout" : ""}`}
      data-label={label}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } }}
    >
      {children}
      {showDot !== undefined && <span className={`kwsb-dot${showDot ? " show" : ""}`} />}
    </span>
  );
}

// ── Notifications bell + dropdown. The dropdown anchors top:100%/right:0 to
// its offset parent (built for the old top bar); from the left rail we host
// it in a fixed, viewport-clamped anchor so it opens beside the icon.
function SidebarNotifications() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [anchor, setAnchor] = useState({ left: 82, top: 24 });
  const wrapRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/api/notifications/unread-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setUnread(d.unread_count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = () => {
    if (!open && wrapRef.current) {
      // Clamp a 320x520 panel into the viewport, opening to the right of the
      // rail (or above the bar on mobile) near the bell.
      const r = wrapRef.current.getBoundingClientRect();
      const vw = window.innerWidth, vh = window.innerHeight;
      const left = Math.min(Math.max(8, r.right + 10), vw - 320 - 8);
      const top = Math.min(Math.max(12, r.top - 8), vh - 528);
      setAnchor({ left, top });
    }
    setOpen((v) => !v);
  };

  return (
    <span ref={wrapRef} style={{ display: "inline-flex" }}>
      <IconBtn label="Notifications" showDot={unread > 0} onClick={toggle}>
        <Bell size={ICON} strokeWidth={STROKE} />
      </IconBtn>
      {open && (
        <div style={{ position: "fixed", left: anchor.left, top: anchor.top, width: 320, height: 0, zIndex: 10070 }}>
          <NotificationDropdown
            onClose={() => setOpen(false)}
            onCountChange={(n) => setUnread(n)}
          />
        </div>
      )}
    </span>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useContext(AuthContext);
  const { isEnabled } = useOrgModules();
  const hasActiveOnboarding = useHasActiveOnboarding();
  const learningOverdue = useLearningOverdueCount();
  const branding = useOrgBranding();

  const norm = decodeURI(String(pathname || "")).toLowerCase().replace(/\/+$/, "") || "/";

  // Role gating — SAME predicates as TeamFab / AdminFab / ExecFab.
  const user = readUser();
  const isManager = !!(user && (user.role === "manager" || user.role === "superadmin" || user.is_manager || user.roles?.includes?.("manager")));
  const isAdmin   = !!(user && (user.is_admin || user.role === "admin" || user.role === "superadmin" || user.roles?.includes?.("admin") || user.roles?.includes?.("superadmin")));
  const isExec    = !!(user && (user.role === "executive" || user.role === "superadmin" || user.roles?.includes?.("executive")));
  // Mirrors routes/RoleRoute.jsx, which gates /admin/* on the PRIMARY role
  // only (superadmin bypasses). Used solely to decide whether an adminHome
  // nav icon may target the admin variant — see the nav map below.
  const canRouteAdmin = !!(user && (user.role === "admin" || user.role === "superadmin"));

  const visibleNav = NAV.filter((n) => {
    if (n.module !== null && !isEnabled(n.module)) return false;
    if (n.newStarterOnly && hasActiveOnboarding !== true) return false;
    return true;
  });

  // Current module → contextual target for the role-view icons (falls back to
  // that view's dashboard when the current page isn't a switchable module).
  const currentNav = NAV.find((n) => navActive(n, norm));
  const teamTarget  = currentNav?.team  || "/manager-dashboard";
  // CASES IS THE ONE MODULE WITH NO NAV ENTRY, so it can never be
  // `currentNav` and the fallback used to send an admin working on cases
  // to the generic dashboard. It stays out of NAV — a subject, reporter,
  // witness or companion must never see a Cases icon in their own
  // navigation, and CasesNavEntry below remains the only thing that
  // renders one — so the module's admin variant is named here instead.
  //
  // matches(), NOT norm.startsWith("/cases"). A bare prefix test also
  // matches "/casework" or "/casesomething"; matches() is segment-aware
  // (norm === route || norm.startsWith(route + "/")), which is the rule
  // every other route comparison in this file already uses. No such path
  // exists in App.js today and the 404 route renders no sidebar anyway,
  // so this guards a future route rather than a present bug — but a
  // second, looser prefix rule in a file that already has a correct one
  // is how the two drift apart.
  const adminTarget =
    currentNav?.admin ||
    (matches(norm, "/cases") ? "/admin/cases" : "/admin/dashboard");
  const execTarget  = currentNav?.key === "learning" ? "/executive/dashboard?tab=learning" : "/executive/dashboard";

  const teamActive  = norm.startsWith("/manager");
  const adminActive = norm.startsWith("/admin");
  const execActive  = norm.startsWith("/executive");

  // Settings: admins go to the org/admin settings hub (same as the old
  // toolbar); everyone else to the staff Settings page (which hosts My
  // profile + My diary).
  const settingsTarget = isAdmin ? "/admin/documents" : "/messenger/settings";

  const logoUrl = branding?.logo_url || null;
  const orgName = branding?.name || null;

  return (
    <>
      <style>{SIDEBAR_CSS}</style>
      <aside className="kwsb-sidebar" aria-label="Primary navigation">

        {/* tenant org logo — image if present, else name initials on the
            gradient; generic gradient badge while loading / on error. */}
        <div
          className="kwsb-tenant"
          title={orgName || "Organisation"}
          role="button"
          tabIndex={0}
          aria-label={orgName ? `${orgName} — dashboard` : "Dashboard"}
          onClick={() => navigate("/messenger/dashboard")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/messenger/dashboard"); } }}
        >
          {logoUrl ? <img src={logoUrl} alt={orgName || "Organisation logo"} /> : initialsFrom(orgName)}
        </div>

        <div className="kwsb-spacer" />

        {/* nav modules — vertically centred between the two spacers */}
        <nav className="kwsb-nav">
          {visibleNav.map((item) => {
            const active = navActive(item, norm);
            const isLearning = item.key === "learning";
            // adminHome entries (see NAV): send admins straight to the admin
            // variant, unless they're already on it — then the icon acts as
            // the way back to their own staff view.
            // Uses canRouteAdmin (the RoleRoute predicate), NOT isAdmin: the
            // admin routes gate on the PRIMARY role only, so an admin-by-
            // secondary-role would be bounced to /403. Pointing the icon by
            // the same rule the route enforces avoids sending anyone into a
            // dead end. Access itself is unchanged — this only picks a target.
            const goAdmin = !!(item.adminHome && canRouteAdmin && item.admin && !matches(norm, item.admin));
            const target  = goAdmin ? item.admin : item.staff;
            const label = isLearning && learningOverdue > 0
              ? `Learning · ${learningOverdue} overdue`
              : goAdmin
                ? `${item.label} · Admin`
                : item.label;
            const dotProps = isLearning ? { showDot: learningOverdue > 0 } : {};
            return (
              <IconBtn
                key={item.key}
                label={label}
                active={active}
                onClick={() => navigate(target)}
                {...dotProps}
              >
                <item.Icon size={ICON} strokeWidth={STROKE} />
              </IconBtn>
            );
          })}

          {/* Cases — NOT part of visibleNav, and not filtered here. It
              renders itself or nothing; see the block above
              useShowCasesEntry for why the gate lives inside the
              component rather than in the filter above.

              `active` is computed here rather than through navActive,
              because that helper reads item.staff / item.team /
              item.admin off a NAV entry and this deliberately is not
              one. */}
          <CasesNavEntry
            render={({ label, Icon, onClick }) => (
              <IconBtn
                label={label}
                onClick={onClick}
                active={matches(norm, "/cases")}
              >
                <Icon size={ICON} strokeWidth={STROKE} />
              </IconBtn>
            )}
          />
        </nav>

        <div className="kwsb-spacer" />

        {/* utility + role views */}
        <div className="kwsb-util">
          {isManager && (
            <IconBtn label="Team view" active={teamActive} onClick={() => navigate(teamTarget)}>
              <Users size={ICON} strokeWidth={STROKE} />
            </IconBtn>
          )}
          {isAdmin && (
            <IconBtn label="Admin view" active={adminActive} onClick={() => navigate(adminTarget)}>
              <ShieldCheck size={ICON} strokeWidth={STROKE} />
            </IconBtn>
          )}
          {isExec && (
            <IconBtn label="Exec view" active={execActive} onClick={() => navigate(execTarget)}>
              <Briefcase size={ICON} strokeWidth={STROKE} />
            </IconBtn>
          )}

          <div className="kwsb-div" />

          <SidebarNotifications />

          <IconBtn label="Settings" active={norm === "/admin/documents"} onClick={() => navigate(settingsTarget)}>
            <Settings size={ICON} strokeWidth={STROKE} />
          </IconBtn>

          <IconBtn
            label="Logout"
            logout
            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          >
            <LogOut size={ICON} strokeWidth={STROKE} />
          </IconBtn>
        </div>
      </aside>
    </>
  );
}

// ── spec CSS, scoped with a kwsb- prefix so it can't collide with app styles.
// Values copied verbatim from design_specs/dashboard_v7.html.
const SIDEBAR_CSS = `
.kwsb-sidebar{
  position:fixed;left:0;top:0;bottom:0;width:72px;z-index:40;
  background:rgba(5,5,5,.95);border-right:1px solid rgba(0,255,153,.30);
  display:flex;flex-direction:column;align-items:center;padding:14px 0;
}
.kwsb-tenant{
  width:48px;height:48px;border-radius:14px;overflow:hidden;flex:none;
  border:1px solid rgba(255,255,255,.10);cursor:pointer;
  background:linear-gradient(135deg,#00FF99,#00BFFF);
  display:grid;place-items:center;
  font-family:'Space Grotesk','Inter',sans-serif;font-weight:700;font-size:19px;color:#000;
}
.kwsb-tenant img{width:100%;height:100%;object-fit:cover;display:block}
.kwsb-nav,.kwsb-util{display:flex;flex-direction:column;align-items:center;gap:4px}
.kwsb-div{width:26px;height:1px;background:rgba(255,255,255,.10);margin:6px 0}
.kwsb-spacer{flex:1}
.kwsb-nv{
  position:relative;width:40px;height:40px;border-radius:12px;
  display:grid;place-items:center;cursor:pointer;color:rgba(255,255,255,.55);
  border:1px solid transparent;transition:.15s;
}
.kwsb-nv svg{width:19px;height:19px;flex:none}
.kwsb-nv:hover{color:#fff;background:rgba(255,255,255,.05)}
.kwsb-nv.is-active{
  color:#00FF99;background:rgba(0,255,153,.12);border-color:rgba(0,255,153,.30);
  box-shadow:0 0 14px rgba(0,255,153,.35), inset 0 0 8px rgba(0,255,153,.12);
}
.kwsb-nv.is-logout:hover{color:#ff5f56;background:rgba(255,95,86,.08)}
.kwsb-nv .kwsb-dot{
  position:absolute;bottom:6px;right:7px;width:6px;height:6px;border-radius:99px;
  background:#00FF99;box-shadow:0 0 6px rgba(0,255,153,.7);display:none;
}
.kwsb-nv .kwsb-dot.show{display:block}
.kwsb-nv::after{
  content:attr(data-label);position:absolute;left:calc(100% + 10px);top:50%;
  transform:translateY(-50%) translateX(-4px);
  background:#131313;border:1px solid rgba(255,255,255,.10);color:#fff;
  font-size:10.5px;font-weight:600;padding:5px 11px;border-radius:99px;white-space:nowrap;
  opacity:0;pointer-events:none;transition:.15s;z-index:60;
}
.kwsb-nv:hover::after{opacity:1;transform:translateY(-50%) translateX(0)}
.kwsb-content{margin-left:72px;padding:24px 24px 24px;min-height:100vh}
@media (max-width:720px){
  .kwsb-content{margin-left:0;padding:16px 16px 82px}
  .kwsb-sidebar{
    top:auto;bottom:0;left:0;right:0;width:auto;height:62px;z-index:100;
    flex-direction:row;justify-content:space-around;padding:0 6px;
    border-right:none;border-top:1px solid rgba(0,255,153,.30);overflow-x:auto;
  }
  .kwsb-nav,.kwsb-util{flex-direction:row;gap:0}
  .kwsb-tenant,.kwsb-div,.kwsb-spacer{display:none}
  .kwsb-nv::after{left:50%;top:auto;bottom:calc(100% + 8px);transform:translateX(-50%) translateY(3px)}
  .kwsb-nv:hover::after{transform:translateX(-50%) translateY(0)}
}
`;
